import {
  ClipboardList,
  ExternalLink,
  FileSignature,
  GraduationCap,
  PenSquare,
  MessageSquare,
  Award,
  Sparkles,
  Lock,
  CheckCircle2,
  Clock,
} from 'lucide-react';

/**
 * Page DOMAIN — Forms (librairie centrale)
 *
 * Liste TOUS les formulaires existants dans le système, qu'ils soient :
 *  - actifs (Qualiopi V2, Émargement v4 Apps Script, Diagnostic Phase 54)
 *  - en cours (en préparation, mais déjà identifiés)
 *  - planifiés (vision future)
 *
 * Chaque card montre :
 *  - le nom + description fonctionnelle
 *  - le destinataire (apprenant / formateur / public)
 *  - le statut (actif / en prep / planifié)
 *  - l'URL d'accès au template (Apps Script, Drive, ou endpoint API)
 *  - le domaine consommateur (qui utilise ce formulaire)
 *
 * À terme : actions "Générer un lien pour {apprenant}" qui forge un token
 * signé + URL personnalisée → /domains/forms/engine/ + table FormInstance.
 */

interface FormDef {
  id: string;
  name: string;
  code?: string;
  category: 'Qualiopi' | 'Émargements' | 'Pédagogie' | 'Formateurs' | 'Élèves' | 'Admin';
  description: string;
  recipient: 'apprenant' | 'formateur' | 'public' | 'apprenant+formateur';
  status: 'actif' | 'en prep' | 'planifié';
  templateUrl: string | null;
  consumerDomain: string;
  notes?: string;
}

const FORMS: FormDef[] = [
  // ── QUALIOPI V2 — 7 formulaires actifs en Postgres avec tokens HMAC ──
  {
    id: 'qualiopi-f1',
    code: 'F1',
    name: 'Test de positionnement (entrée)',
    category: 'Qualiopi',
    description: 'Diagnostic adaptatif initial — niveau actuel + objectifs apprenant.',
    recipient: 'apprenant',
    status: 'actif',
    templateUrl: null,
    consumerDomain: '/qualiopi/',
    notes: 'Tokens HMAC en Postgres QualiopiV2Form. Issued auto au validate.',
  },
  {
    id: 'qualiopi-f2',
    code: 'F2',
    name: 'Audit des besoins',
    category: 'Qualiopi',
    description: 'Questionnaire approfondi : projet pro, contraintes, attentes pédagogiques.',
    recipient: 'apprenant',
    status: 'actif',
    templateUrl: null,
    consumerDomain: '/qualiopi/',
  },
  {
    id: 'qualiopi-f3',
    code: 'F3',
    name: 'Démarrage de formation',
    category: 'Qualiopi',
    description: "Confirmation prise de connaissance du règlement intérieur + livret d'accueil.",
    recipient: 'apprenant',
    status: 'actif',
    templateUrl: null,
    consumerDomain: '/qualiopi/',
  },
  {
    id: 'qualiopi-f4',
    code: 'F4',
    name: 'Évaluation mi-parcours',
    category: 'Qualiopi',
    description: 'Auto-évaluation + ajustements pédagogiques à mi-formation.',
    recipient: 'apprenant',
    status: 'actif',
    templateUrl: null,
    consumerDomain: '/qualiopi/',
  },
  {
    id: 'qualiopi-f5',
    code: 'F5',
    name: 'Évaluation de fin de formation',
    category: 'Qualiopi',
    description: 'Bilan compétences acquises + niveau de satisfaction global.',
    recipient: 'apprenant',
    status: 'actif',
    templateUrl: null,
    consumerDomain: '/qualiopi/',
  },
  {
    id: 'qualiopi-f6',
    code: 'F6',
    name: 'Évaluation pédagogique à froid',
    category: 'Qualiopi',
    description: 'Satisfaction 3 mois après fin de formation (mise en application).',
    recipient: 'apprenant',
    status: 'actif',
    templateUrl: null,
    consumerDomain: '/qualiopi/',
  },
  {
    id: 'qualiopi-f7',
    code: 'F7',
    name: 'Évaluation par le formateur',
    category: 'Qualiopi',
    description: "Bilan du formateur sur la progression de l'apprenant + attestation.",
    recipient: 'formateur',
    status: 'actif',
    templateUrl: null,
    consumerDomain: '/qualiopi/',
  },

  // ── ÉMARGEMENTS — Apps Script v4 (livré 23/05/2026) ──
  {
    id: 'emargement-v4',
    name: 'Émargement séance v4',
    category: 'Émargements',
    description:
      "Prof remplit la séance → email à l'élève → signature électronique → PDF déposé dans le dossier Drive de l'apprenant. 20 colonnes classeur, commentaire élève obligatoire si note ≤ 3.",
    recipient: 'apprenant+formateur',
    status: 'actif',
    templateUrl:
      'https://script.google.com/macros/s/AKfycbyWyBWXc6LpAyADiWTGXr3Uu3vde9qqsHByCI_VW3WPqcXbkffryXrDqgSwpDz_lJQg/exec?role=prof&token=47dcd6d8721a4df1ad40f64f',
    consumerDomain: '/emargements/ (à créer)',
    notes:
      'Apps Script existant. À wirer dans /domains/emargements/ + slice EmargementCard fiche apprenant.',
  },

  // ── DIAGNOSTIC — Phase 54 (livrée 26/05/2026) ──
  {
    id: 'diag-entry',
    code: 'DIAG_ENTRY',
    name: 'Diagnostic adaptatif — Entrée',
    category: 'Pédagogie',
    description:
      "Moteur adaptatif 7 niveaux (ALPHAB → C2), 209 questions, timer 15/30s. Filtre débutants à l'entrée de formation.",
    recipient: 'apprenant',
    status: 'actif',
    templateUrl: 'https://cockpit.andalous-elearning.com/diagnostic?mode=DIAG_ENTRY',
    consumerDomain: '/pedagogie/',
    notes: 'Wrapper DIAG_ENTRY. Sert de F1 amélioré pour les nouveaux apprenants.',
  },
  {
    id: 'diag-exit',
    code: 'DIAG_EXIT',
    name: 'Diagnostic adaptatif — Sortie',
    category: 'Pédagogie',
    description:
      'Bilan de fin de formation. Compare au DIAG_ENTRY → progression en paliers franchis.',
    recipient: 'apprenant',
    status: 'actif',
    templateUrl: 'https://cockpit.andalous-elearning.com/diagnostic?mode=DIAG_EXIT',
    consumerDomain: '/pedagogie/',
  },
  {
    id: 'diag-public',
    code: 'DIAG_PUBLIC',
    name: 'Diagnostic public (lead magnet)',
    category: 'Pédagogie',
    description:
      'Version publique = lead magnet site rkformations.fr. Génère un Lead Zoho + CTA Bookings auto.',
    recipient: 'public',
    status: 'actif',
    templateUrl: 'https://rkformations.fr/diagnostic',
    consumerDomain: '/pedagogie/ + /leads/',
    notes: 'Rate-limit 1 passage/mois/email côté API.',
  },

  // ── À VENIR ──
  {
    id: 'note-prof',
    name: 'Note formateur sur élève',
    category: 'Formateurs',
    description:
      'Mini-formulaire post-séance : appréciation, points bloquants, objectifs séance suivante. Permet de suivre la qualité pédagogique au fil du temps.',
    recipient: 'formateur',
    status: 'en prep',
    templateUrl: null,
    consumerDomain: '/formateurs/ (à créer)',
  },
  {
    id: 'retour-eleve',
    name: 'Retour libre élève',
    category: 'Élèves',
    description:
      'Feedback libre apprenant : sur le formateur, le rythme, les supports, la plateforme.',
    recipient: 'apprenant',
    status: 'en prep',
    templateUrl: null,
    consumerDomain: '/communications/',
  },
  {
    id: 'convention-signable',
    name: 'Convention signable en ligne (non-CPF)',
    category: 'Admin',
    description:
      'Pour les conventions OPCO/Corporate/Direct : remplissage + signature électronique → génère le PDF déposé dans 01-Convention.',
    recipient: 'apprenant',
    status: 'planifié',
    templateUrl: null,
    consumerDomain: '/documents/',
  },
  {
    id: 'attestation-presence-evenement',
    name: 'Attestation présence évènement / examen',
    category: 'Admin',
    description:
      "Signature en présentiel d'une attestation de présence à un évènement ou un examen.",
    recipient: 'apprenant',
    status: 'planifié',
    templateUrl: null,
    consumerDomain: '/documents/',
  },
];

const CATEGORY_META: Record<FormDef['category'], { color: string; icon: React.ReactNode }> = {
  Qualiopi: { color: 'emerald', icon: <Award className="h-4 w-4" /> },
  Émargements: { color: 'sky', icon: <FileSignature className="h-4 w-4" /> },
  Pédagogie: { color: 'violet', icon: <GraduationCap className="h-4 w-4" /> },
  Formateurs: { color: 'amber', icon: <PenSquare className="h-4 w-4" /> },
  Élèves: { color: 'rose', icon: <MessageSquare className="h-4 w-4" /> },
  Admin: { color: 'neutral', icon: <Lock className="h-4 w-4" /> },
};

export function FormsPage() {
  const actifs = FORMS.filter((f) => f.status === 'actif');
  const enPrep = FORMS.filter((f) => f.status === 'en prep');
  const planifies = FORMS.filter((f) => f.status === 'planifié');

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      {/* Header */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
          Domaine — Infrastructure cross-cutting
        </div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-neutral-900">
          <ClipboardList className="text-primary-600 h-6 w-6" />
          Formulaires
        </h1>
        <p className="mt-1 text-sm text-neutral-600">
          Librairie centrale des {FORMS.length} formulaires du système — Qualiopi V2 (F1→F7),
          Émargement v4 Apps Script, Diagnostic adaptatif Phase 54, Notes formateur, Retours élèves,
          Convention signable.
        </p>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-3 gap-3">
        <StatCard
          icon={<CheckCircle2 className="h-5 w-5 text-emerald-600" />}
          n={actifs.length}
          label="Actifs en production"
          color="emerald"
        />
        <StatCard
          icon={<Sparkles className="h-5 w-5 text-amber-600" />}
          n={enPrep.length}
          label="En préparation"
          color="amber"
        />
        <StatCard
          icon={<Clock className="h-5 w-5 text-neutral-500" />}
          n={planifies.length}
          label="Planifiés"
          color="neutral"
        />
      </div>

      {/* Liste actifs */}
      <Section
        title={`✅ Actifs en production (${actifs.length})`}
        subtitle="Tournent déjà. Cliquables pour ouvrir le template source."
      >
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {actifs.map((f) => (
            <FormCard key={f.id} form={f} />
          ))}
        </div>
      </Section>

      {/* Liste en prep */}
      {enPrep.length > 0 && (
        <Section
          title={`🔧 En préparation (${enPrep.length})`}
          subtitle="Templates identifiés, à coder."
        >
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {enPrep.map((f) => (
              <FormCard key={f.id} form={f} />
            ))}
          </div>
        </Section>
      )}

      {/* Liste planifiés */}
      {planifies.length > 0 && (
        <Section
          title={`📅 Planifiés (${planifies.length})`}
          subtitle="Vision long-terme, ordre à définir."
        >
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {planifies.map((f) => (
              <FormCard key={f.id} form={f} />
            ))}
          </div>
        </Section>
      )}

      {/* Note d'architecture */}
      <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 text-[11px] text-neutral-600">
        <div className="text-primary-700 mb-1 text-[10px] font-bold uppercase tracking-widest">
          🧭 Vision engine central
        </div>
        Actuellement chaque type de formulaire a son propre moteur (Qualiopi V2 = Postgres + HMAC ;
        Émargement = Apps Script ; Diagnostic = backend custom). Quand on aura le temps, on les
        fusionnera derrière un seul engine{' '}
        <code className="rounded bg-white px-1 py-0.5 font-mono">POST /v2/forms/instances</code> qui
        génère un token signé + URL{' '}
        <code className="rounded bg-white px-1 py-0.5 font-mono">/f/{`{token}`}</code> pour
        n'importe quel template + recipient. Un seul moteur, une seule URL pattern, des dizaines de
        formulaires.
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────

function StatCard({
  icon,
  n,
  label,
  color,
}: {
  icon: React.ReactNode;
  n: number;
  label: string;
  color: string;
}) {
  return (
    <div
      className={`flex items-center gap-3 rounded-xl border bg-white p-4 ${
        color === 'emerald'
          ? 'border-emerald-200'
          : color === 'amber'
            ? 'border-amber-200'
            : 'border-neutral-200'
      }`}
    >
      {icon}
      <div>
        <div className="text-2xl font-black text-neutral-900">{n}</div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
          {label}
        </div>
      </div>
    </div>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div>
        <h2 className="text-base font-bold text-neutral-900">{title}</h2>
        <p className="text-[11px] text-neutral-500">{subtitle}</p>
      </div>
      {children}
    </div>
  );
}

function FormCard({ form }: { form: FormDef }) {
  const meta = CATEGORY_META[form.category];
  const colorMap: Record<string, string> = {
    emerald: 'border-emerald-200 bg-emerald-50/30',
    sky: 'border-sky-200 bg-sky-50/30',
    violet: 'border-violet-200 bg-violet-50/30',
    amber: 'border-amber-200 bg-amber-50/30',
    rose: 'border-rose-200 bg-rose-50/30',
    neutral: 'border-neutral-200 bg-neutral-50/30',
  };
  const textColor: Record<string, string> = {
    emerald: 'text-emerald-700',
    sky: 'text-sky-700',
    violet: 'text-violet-700',
    amber: 'text-amber-700',
    rose: 'text-rose-700',
    neutral: 'text-neutral-700',
  };
  const recipientLabel: Record<string, string> = {
    apprenant: '🧑‍🎓 Apprenant',
    formateur: '👨‍🏫 Formateur',
    public: '🌐 Public',
    'apprenant+formateur': '🧑‍🎓 + 👨‍🏫 Duo',
  };
  const statusBadge: Record<string, { bg: string; label: string }> = {
    actif: { bg: 'bg-emerald-100 text-emerald-700', label: '✓ actif' },
    'en prep': { bg: 'bg-amber-100 text-amber-700', label: '🔧 en prep' },
    planifié: { bg: 'bg-neutral-100 text-neutral-600', label: '📅 planifié' },
  };
  const badge = statusBadge[form.status]!;
  return (
    <div className={`flex flex-col rounded-lg border p-3 ${colorMap[meta.color]}`}>
      {/* Header */}
      <div className="mb-1.5 flex items-start justify-between gap-2">
        <div className={`flex items-center gap-1.5 ${textColor[meta.color]}`}>
          {meta.icon}
          <span className="text-[10px] font-bold uppercase tracking-wider">{form.category}</span>
        </div>
        <span
          className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${badge.bg}`}
        >
          {badge.label}
        </span>
      </div>

      {/* Title + code */}
      <div className="text-sm font-bold text-neutral-900">
        {form.code && (
          <span className="mr-1 rounded bg-neutral-200 px-1 py-0 font-mono text-[10px] text-neutral-700">
            {form.code}
          </span>
        )}
        {form.name}
      </div>

      {/* Description */}
      <p className="mt-1 flex-1 text-[11px] text-neutral-600">{form.description}</p>

      {/* Notes spéciales */}
      {form.notes && (
        <p className="mt-1.5 rounded bg-white/60 px-1.5 py-1 text-[10px] italic text-neutral-500">
          📝 {form.notes}
        </p>
      )}

      {/* Footer : recipient + consumer + URL */}
      <div className="mt-2 flex items-center justify-between border-t border-neutral-200/50 pt-2 text-[10px]">
        <div className="space-y-0.5">
          <div className="text-neutral-700">{recipientLabel[form.recipient]}</div>
          <div className="font-mono text-[9px] text-neutral-500">{form.consumerDomain}</div>
        </div>
        {form.templateUrl ? (
          <a
            href={form.templateUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 inline-flex items-center gap-1 rounded border border-neutral-200 bg-white px-2 py-1 font-semibold hover:bg-neutral-50"
          >
            Template <ExternalLink className="h-2.5 w-2.5" />
          </a>
        ) : (
          <span className="italic text-neutral-400">URL Postgres (token)</span>
        )}
      </div>
    </div>
  );
}
